import { API_BASE_URL } from '@synergy/config';
import { Address } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { getAddressDisplayValue, padZeroLeft } from '@synergy/utils';

import { AccountApi } from '../../../api/account-api';
import { AppState } from '../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../pool-autocomplete-list';
import { PoolFormAutocompleteList, PoolFormAutocompleteListProps } from '../../abstract/form-autocomplete-list';
import { User } from '../../../models/user';

type AutocompleteType = 'DeliveryAddress' | 'InvoiceAddress';

interface PoolAccountAddressAutocompleteListReduxProps {
    user: User;
}

interface PoolAccountAddressAutocompleteListOwnProps
    extends Omit<PoolFormAutocompleteListProps<Address, PartialAddress>, 'items'> {
    autocompleteType: AutocompleteType;
    accountId?: number;
}

type PoolAccountAddressAutocompleteListProps = PoolAccountAddressAutocompleteListOwnProps &
    PoolAccountAddressAutocompleteListReduxProps;

export interface PartialAddress extends BaseAutocompleteSuggestionType, Pick<Address, 'street' | 'title' | 'zip'> {}

class PoolAccountAddressAutocompleteListBase extends PoolFormAutocompleteList<
    Address,
    PartialAddress,
    PoolAccountAddressAutocompleteListProps
> {
    constructor(props: PoolAccountAddressAutocompleteListProps) {
        super(props);
        this.hideId = true;
    }

    private messageId = {
        DeliveryAddress: 'app.label.deliveryAddress',
        InvoiceAddress: 'app.label.invoiceAddress',
    };

    private api = new AccountApi(API_BASE_URL);
    private apiAbortController?: AbortController;

    public label = (<FormattedMessage id={this.messageId[this.props.autocompleteType]} />);

    private getFilteredAddresses = (items: Address[]) => {
        switch (this.props.autocompleteType) {
            case 'DeliveryAddress': {
                return items.filter(address => address.isDeliveryAddress);
            }
            case 'InvoiceAddress': {
                return items.filter(address => address.isInvoiceAddress);
            }
            default:
                return [];
        }
    };

    public componentDidMount() {
        this.setState({ items: [] });
    }

    public componentDidUpdate(prevProps: PoolAccountAddressAutocompleteListProps) {
        if (this.props.accountId !== prevProps.accountId) {
            this.setState({ items: [] });
        }
    }

    public async handleValueChanged(search: string) {
        if (!this.props.accountId) {
            this.setState({ items: [] });
            return;
        }
        this.apiAbortController?.abort();
        this.apiAbortController = new AbortController();
        const response = await this.api.getAddresses(
            this.props.accountId,
            search,
            this.props.autocompleteType === 'InvoiceAddress',
            this.apiAbortController.signal
        );
        if (!response.success && response.requestCanceled) {
            return;
        }

        const items = response.success === true ? response.result : [];
        this.setState({ items });
    }

    public getSuggestionValue = (suggestion: PartialAddress) => suggestion && `${suggestion.street}, ${suggestion.zip}`;

    public getDisplayValue(address: Address) {
        return getAddressDisplayValue(address);
    }

    public getPartial = (items: Address[]): PartialAddress[] => {
        return this.getFilteredAddresses(items).map(address => {
            return {
                id: padZeroLeft(address.id, 5),
                title: address.title ?? '',
                street: [address.street, address.number, address.addition].filter(Boolean).join(' '),
                zip: `${address.zip} ${address.city}`,
            };
        });
    };
}

const mapStateToProps = (state: AppState) => ({
    user: state.user,
});

export const PoolAccountAddressAutocompleteList = connect(mapStateToProps)(PoolAccountAddressAutocompleteListBase);
